import {
  type ChangeEventHandler,
  type DetailedHTMLProps,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useCallback,
  useId,
  useMemo,
} from 'react';

import clsx from 'clsx';

export interface SwitchProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'type'
  > {
  text?: string;
  children?: ReactNode;
  handleChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * `children` prop has priority over `text` in order to keep compatibility with previous component usages.
 * Use `flex-row-reverse` and `justify-between` as you see fit.
 */
const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { text, children, handleChange, onChange, id, className, ...props },
  ref
) {
  const internalOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onChange && onChange(e);
      handleChange && handleChange(e);
    },
    [onChange, handleChange]
  );
  const gID = useId();
  const iID = useMemo(() => id || text || gID, [id, text, gID]);
  return (
    <span className={clsx('flex shrink-0 items-stretch gap-2', className)}>
      <div className="relative inline-block w-9 shrink-0">
        <input
          type="checkbox"
          className="peer z-10 h-full w-full cursor-pointer opacity-0"
          onChange={internalOnChange}
          id={iID}
          ref={ref}
          {...props}
        />
        <span
          className={clsx(
            `pointer-events-none absolute bottom-0 left-0 right-0 top-0 my-auto h-4
            rounded-[32px] bg-gray-100 duration-300 before:absolute before:bottom-0.5
            before:left-1 before:h-3 before:w-3 before:rounded-full before:bg-gray-300
            before:duration-300 peer-checked:bg-green-200 peer-checked:before:translate-x-4
            peer-checked:before:bg-green-600
            peer-focus:shadow-[0_0_3px_theme(colors.brand.focus)] peer-disabled:bg-gray-500
            dark:bg-gray-600 dark:peer-disabled:before:bg-gray-700`
          )}
        />
      </div>
      <label
        className="text-background-dark my-auto hyphens-auto break-words text-base dark:text-white"
        htmlFor={iID}
      >
        {children || text}
      </label>
    </span>
  );
});

Switch.displayName = 'Switch';

export { Switch };
